import useSWR from 'swr';
import { useContext, useEffect } from 'react';
import { NavigationContext } from '../../Navigation/NavigationProvider';
import { useOrganization } from '../../Settings/SettingsDropDownMenu';
import { fetchService } from '../../../utils/fetchService';
import { GuardianReportHeaderAndMetadata } from './GuardianReportHeaderAndMetadata';
import PolicyViolations from './PolicyViolations/PolicyViolations';
import { ActionsButtonHeader } from './ActionsButtonHeader';
import { RootTabs } from './tabs/RootTabs';
import { transformRuleSummaries } from './utils/dopplerUtil';
import { useFetchRuleSummaries } from './utils/useFetchRuleSummaries';

const fetcher = accessToken => path => fetchService(path, accessToken);

export function SarifReportContent({ analysisId, accessToken, backQParams }) {
  const {
    data: { appDetailsResponse, aggregateData, sbomData },
  } = useSWR(
    [`/eval/applications/appDetails/${analysisId}`, `/eval/results/${analysisId}/aggregate-data`, `/eval/results/${analysisId}/sbom`],
    fetchDataInParallel(accessToken),
    {
      suspense: true,
    },
  );

  const { ruleSummariesData } = useFetchRuleSummaries(analysisId, { suspense: true });

  const { data: deniedAppsResponse, mutate: deniedAppsMutate } = useSWR('/list/denied-apps', fetcher(accessToken), { suspense: true });
  const { data: trustedAppsResponse, mutate: trustedAppsMutate } = useSWR('/list/trusted-apps', fetcher(accessToken), { suspense: true });

  const deniedApps = deniedAppsResponse?.deniedApps || [];
  const trustedApps = trustedAppsResponse?.trustedApps || [];

  const { appDetails, appAnalysis } = appDetailsResponse;
  // Fetch App Details
  const { appName, appId, version, os } = appDetails;
  const iconUrl = appDetails.appIconBase64 ? `data:image/png;base64,${appDetails.appIconBase64}` : '';
  const triggeredPolicies = appAnalysis?.orgPolicyResults?.filter((p: any) => p.didTrigger) || [];
  const hasPolicyViolation = triggeredPolicies.length > 0;

  const ruleSummaries = transformRuleSummaries(ruleSummariesData);

  const malwareRule = ruleSummaries?.find(ruleSummary => ruleSummary.rule?.id?.startsWith('malware/'));

  const { data: maliciousData } = useSWR(
    malwareRule?.guid ? `/eval/results/${analysisId}/evidence?ruleGuid=${malwareRule?.guid}` : null,
    fetcher(accessToken),
  );
  const maliciousResult = maliciousData?.entries && maliciousData.entries.length > 0 ? maliciousData.entries[0].result : {};

  const { setAppName } = useContext(NavigationContext);

  const { orgData } = useOrganization(accessToken);
  const showDeniedApps = orgData?.settings?.includes('denied-apps');

  const isDeniedApp = showDeniedApps && deniedApps.some((d: { bundleId: string }) => d.bundleId === appId);
  const isTrustedApp = trustedApps.some((t: { bundleId: string }) => t.bundleId === appId);

  useEffect(() => {
    setAppName(appName);
  });

  let tag: { label: string; color: string; iconSrc: string } | undefined;

  if (isDeniedApp) {
    tag = { label: 'Denied App', color: '#FF0000', iconSrc: '/icons/shield-x-red.svg' };
  } else if (isTrustedApp) {
    tag = { label: 'Trusted App', color: '#00B300', iconSrc: '/icons/shield-checked-green.svg' };
  } else if (hasPolicyViolation) {
    tag = { label: 'Policy Violation', color: '#FF0000', iconSrc: '/icons/alert-triangle-red.svg' };
  }

  return (
    <>
      <ActionsButtonHeader
        deniedApps={deniedApps}
        trustedApps={trustedApps}
        appDetails={appDetails}
        fetchDeniedApps={deniedAppsMutate}
        fetchTrustedApps={trustedAppsMutate}
        showDeniedApps={showDeniedApps}
        backQParams={backQParams}
      />
      <GuardianReportHeaderAndMetadata
        appName={appName}
        appId={appId}
        appVersion={version}
        os={os}
        iconUrl={iconUrl}
        appDetails={appDetails}
        appAnalysis={appAnalysis}
        tag={tag}
      />
      <PolicyViolations orgPolicyResults={appAnalysis?.orgPolicyResults} riskIndicators={appDetails?.iors} />

      <RootTabs
        aggregateData={aggregateData}
        ruleSummaries={ruleSummaries}
        analysisId={analysisId}
        maliciousResult={maliciousResult}
        os={os}
        appDetails={appDetails}
        sbomData={sbomData}
      />
    </>
  );
}

function fetchDataInParallel(accessToken: string) {
  return async (url0, url1, url2) => {
    const [appDetailsResponse, aggregateData, sbomData] = await Promise.all([
      fetchService(url0, accessToken),
      fetchService(url1, accessToken),
      fetchService(url2, accessToken),
    ]);
    return {
      appDetailsResponse,
      aggregateData,
      sbomData,
    };
  };
}
